import {Autocomplete, Box, FormLabel, Grid} from "@mui/material";
import React, {FC} from "react";
import * as z from "zod";
import {Controller, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import TextField from "@mui/material/TextField";
import {useDialogModal, useGlobalActions} from "@/store/global.c";
import {DialogFooter} from "@/widget/DialogModal/DialogModal";
import {emptyValue} from "@/helper/method";
import {useAuditUser, useQueryUser, UserVO} from "@/service/user.s";
import {isNumber} from "lodash";
import {SearchData} from "@/widget/SearchData/SearchData";
import {CustomerPO} from "@/model/customer";
import FormControl from "@mui/material/FormControl";
import {DictPO} from "@/model/system";
import {useAllDict} from "@/store/dict.c";

export interface AuditMemberProps {
}

const validationSchema = z.object({
    userId: z.unknown(),
    username: z.unknown(),
    mobile: z.unknown(),
    remark: z.unknown(),
    customerName: z.string().nonempty("请选择绑定客户"),
    customerId: z.number({message: "请选择绑定客户"}),
    statusId: z.unknown(),
    status: z.string({message: '请选审核状态'}).nonempty({message: '请选择审核状态'}),
});

export type AuditMemberForm = z.infer<typeof validationSchema>
const defaultValues = emptyValue(validationSchema)

export const AuditMember: FC<AuditMemberProps> = () => {
    const {mutateAsync: auditUserMutate, isPending: auditUserPending} = useAuditUser();
    const {id: dialogId} = useDialogModal()
    const {data: userData} = useQueryUser({id: dialogId!}, isNumber(dialogId))
    const user: UserVO = userData?.data || {} as UserVO
    const {auditMember} = useAllDict()
    const {
        handleSubmit,
        register,
        reset,
        setValue,
        control,
        formState: {errors},
    } = useForm<AuditMemberForm>({
        resolver: zodResolver(validationSchema),
        values: {...defaultValues, ...user},
    });
    const {setToast} = useGlobalActions()
    const {setDialogModal} = useGlobalActions()

    const onSubmit = async (values: AuditMemberForm) => {
        const result = await auditUserMutate({...values, userId: dialogId})
        if (result.code == 0) {
            setToast({message: result.msg, severity: 'success', open: true, duration: 2000});
            setDialogModal({open: false})
        } else {
            setToast({message: result.msg, open: true, severity: 'error', duration: 2000});
        }
    }

    return <Box component={'form'} display={'flex'} flexDirection={'column'} rowGap={2} noValidate onSubmit={handleSubmit(onSubmit)}>
        <Grid spacing={2} container width={'inherit'}>
            <Grid size={6}>
                <TextField
                    {...register('username')}
                    placeholder="名称"
                    slotProps={{input: {readOnly: true}}}
                    error={!!errors?.username}
                    helperText={errors?.username?.message}
                    fullWidth
                />
            </Grid>
            <Grid size={6}>
                <TextField
                    {...register('mobile')}
                    type={'number'}
                    error={!!errors?.mobile}
                    slotProps={{input: {readOnly: true}}}
                    helperText={errors?.mobile?.message}
                    placeholder="联系电话"
                    fullWidth
                />
            </Grid>
            <Grid size={12}>
                <TextField
                    multiline
                    rows={3}
                    slotProps={{
                        input: {
                            readOnly: true,
                            sx: {height: 80},
                            inputProps: {maxLength: 250},
                        },
                    }}
                    {...register('remark')}
                    placeholder="备注说明"
                    fullWidth
                />
            </Grid>
            <Grid size={6}>
                <SearchData<AuditMemberForm, CustomerPO> required={true} control={control} attr={"customerName"} attrId={"customerId"} errMsg={errors.customerId?.message}
                                                         setValue={setValue}
                                                         label={"绑定客户"}/>
            </Grid>
            <Grid size={6}>
                <FormControl fullWidth={true} required={true}>
                    <FormLabel>审核结果</FormLabel>
                    <Controller name={"status"} control={control} render={({field: {onChange, value}}) => <Autocomplete
                        value={value as any}
                        onChange={(event, option: DictPO | null) => {
                            setValue("statusId", option?.value)
                            onChange(option?.label)
                        }}
                        options={auditMember || []}
                        fullWidth
                        renderInput={(params) => <TextField placeholder={'请选择审核结果'} error={!!errors?.status}
                                                            helperText={errors?.status?.message} {...params}/>}
                    />}/>
                </FormControl>
            </Grid>
        </Grid>
        <DialogFooter onReset={() => reset(defaultValues)} loading={auditUserPending}/>
    </Box>
}